import { Card, CardBody, Col, Label, Row, Spinner } from "reactstrap";
import React, { useState, useRef, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { formatExpenseDate } from "../../helpers/utils";
import PageHeader from "../common/PageHeader";
import FalconCardHeader from "../common/FalconCardHeader";
import withRedirect from "../../helpers/hoc/withRedirect";
import ButtonIcon from "../common/ButtonIcon";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { fetchPropertyPlanilla } from "../../helpers/api/finance";
import PeriodSelect from "../common/PeriodSelect";
import { useSelector } from "react-redux";
import SupportLink from "../root/support/components/SupportLink";
import { useReactToPrint } from "react-to-print";
// eslint-disable-next-line import/no-unresolved
import { checkUserToken } from "../../helpers/api/auth";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import html2canvas from "html2canvas";
// eslint-disable-next-line import/no-named-as-default
import jsPDF from "jspdf";
import { CurrencyContext } from "../../context/Context";
import PropertyPlanillaReport from "./PropertyPlanillaReport";

const ReportsProperty = () => {
  const reportRef = useRef();
  const { selectedCondominium } = useSelector((state) => state.condominium);
  const { currency, baseCurrency } = useContext(CurrencyContext);
  const { token } = useSelector((state) => state.auth.user);

  const [profile, setProfile] = useState(null);
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const response = await checkUserToken(token);
        if (isMounted) setProfile(response);
      } catch (error) {
        if (isMounted) handleErrorMessage(error);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [token]);

  const propertyId = profile?.propertyId ?? null;
  const condominiumId =
    selectedCondominium ??
    profile?.condominium?.condominium_id ??
    profile?.condominium?.id ??
    profile?.condominium_id ??
    null;
  const today = moment().format("YYYY-MM-DD");
  const [periodos, setPeriodos] = useState({});
  const [params, setParams] = useState({ period_id: null });
  const [planilla, setPlanilla] = useState(null);
  const rateValue = Number(planilla?.currencyContext?.rate || 0);
  const rateDate = planilla?.currencyContext?.rate_date || currency?.date || currency?.created_at;
  const rateDateLabel = rateDate ? formatExpenseDate(rateDate) : "";

  const getRateForRow = (row) => {
    const rowRate = Number(row?.exchange_rate?.value || 0);

    return rowRate > 0 ? rowRate : rateValue;
  };
  const [loadingPlanilla, setLoadingPlanilla] = useState(false);

  useEffect(() => {
    if (!params.period_id || !propertyId) return undefined;

    let isMounted = true;
    setLoadingPlanilla(true);
    setPlanilla(null);

    fetchPropertyPlanilla(propertyId, {
      period_id: params.period_id,
      exchange_rate_date: today,
    })
      .then((response) => {
        if (isMounted) setPlanilla(response?.planilla || null);
      })
      .catch((error) => {
        if (isMounted) {
          setPlanilla(null);
          handleErrorMessage(error);
        }
      })
      .finally(() => {
        if (isMounted) setLoadingPlanilla(false);
      });

    return () => {
      isMounted = false;
    };
  }, [params.period_id, propertyId, today]);

  const tables = planilla?.tables;
  const hasPlanilla = Boolean(
    tables &&
      ["ordinary", "extraordinary", "particular", "funds", "credits"].some(
        (key) => (tables[key] || []).length > 0
      )
  );

  const handlePrint = useReactToPrint({
    content: () => reportRef.current,
  });

  const handleDownload = async () => {
    const canvas = await html2canvas(reportRef.current, {
      windowWidth: reportRef.current.scrollWidth,
      windowHeight: reportRef.current.scrollHeight,
    });
    const imgData = canvas.toDataURL("image/png");

    // eslint-disable-next-line new-cap
    const pdf = new jsPDF("p", "mm", "a4");

    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);

    pdf.save("Reportes.pdf");
  };

  return (
    <>
      <PageHeader
        title="Reportes"
        titleTag="h2"
        description="Gestiona y administra cada una de tus finanzas."
        className="mb-3"
        col={{ lg: 9 }}
      >
        <SupportLink />
      </PageHeader>
      <Card>
        <FalconCardHeader title="Reportes">
          <Row className="justify-content-start align-items-center">
            <Col className="my-2 my-md-0 text-left text-md-left">
              <Label>Seleccionar período</Label>
            </Col>
            <Col md={5} sm={12} className="my-2 my-md-0">
              <PeriodSelect
                condominiumId={condominiumId}
                value={periodos.period_id}
                onChange={(newId, selectedPeriod) => {
                  setPeriodos((prev) => ({
                    ...prev,
                    period_id: selectedPeriod ? selectedPeriod.id : "",
                    period_name: selectedPeriod ? selectedPeriod.name : "",
                    period_date: selectedPeriod ? selectedPeriod.end_at : "",
                  }));
                }}
                placeholder="Selecciona un período"
              />
            </Col>
            <Col md={4} sm={12} className="my-2 my-md-0 text-left text-md-left">
              <ButtonIcon
                onClick={() =>
                  setParams((prev) => ({
                    ...prev,
                    period_id: periodos.period_id,
                  }))
                }
                className="text-nowrap"
                disabled={!periodos.period_id}
                icon={faSearch}
                color="falcon-default"
              >
                {""}
                Buscar
              </ButtonIcon>
            </Col>
          </Row>
        </FalconCardHeader>
      </Card>
      {!params.period_id ? (
        <Card className="mb-3">
          <CardBody className="text-center py-6">
            <h5 className="fs-0 text-700 mb-2">
              Todavía no hay nada que mostrar
            </h5>
            <span className="text-600">
              Selecciona un período y pulsa Buscar para ver tu planilla de
              liquidación de gastos.
            </span>
          </CardBody>
        </Card>
      ) : loadingPlanilla || !profile ? (
        <Card className="mb-3">
          <CardBody className="text-center py-6">
            <Spinner color="primary" />
            <p className="text-600 mb-0 mt-3">
              Consultando tu planilla de liquidación de gastos...
            </p>
          </CardBody>
        </Card>
      ) : !hasPlanilla ? (
        <Card className="mb-3">
          <CardBody className="text-center py-6">
            <h5 className="fs-0 text-700 mb-2">
              Este período todavía no tiene una planilla emitida
            </h5>
            <span className="text-600">
              Cuando el administrador emita la planilla de este período la
              verás aquí.
            </span>
          </CardBody>
        </Card>
      ) : (
        <>
          <Card className="mb-3">
            <CardBody>
              <Row className="justify-content-between align-items-center d-flex">
                <Col md>
                  <h5 className="mb-2 mb-md-0">Descargar reporte</h5>
                </Col>
                <Col
                  xs="auto"
                  lg={"auto"}
                  md={12}
                  className="justify-self-end d-flex justify-content-end align-items-center"
                >
                  <ButtonIcon
                    color="falcon-default"
                    size="sm"
                    icon="arrow-down"
                    onClick={handleDownload}
                    className="mr-2 mb-2 mb-sm-0"
                  >
                    Descargar (.pdf)
                  </ButtonIcon>
                  <ButtonIcon
                    onClick={handlePrint}
                    color="falcon-default"
                    size="sm"
                    icon="print"
                    className="mr-2 mb-2 mb-sm-0"
                  >
                    Imprimir
                  </ButtonIcon>
                </Col>
              </Row>
            </CardBody>
          </Card>
          <Card
            className="mb-3"
            innerRef={(e) => {
              reportRef.current = e;
            }}
          >
            <CardBody>
              <PropertyPlanillaReport
                planilla={planilla}
                baseCurrencySymbol={baseCurrency?.symbol}
                quoteCurrencyIso={currency?.currency?.iso}
                rateValue={rateValue}
                rateDateLabel={rateDateLabel}
                appliedRateDateLabel={rateDateLabel}
                showItemsInQuote
                getRateForRow={getRateForRow}
              />
            </CardBody>
          </Card>
        </>
      )}
    </>
  );
};

ReportsProperty.propTypes = {
  showPagination: PropTypes.bool,
};

export default withRedirect(ReportsProperty);
